/**
 * Bracket utilities for Wright Picks
 * Handles game generation and bracket advancement logic
 */

import { supabase } from "../supabaseClient"

// Round 1 matchups by seed (standard NCAA bracket)
export const ROUND_1_MATCHUPS: [number, number][] = [
  [1, 16],  // Game 1
  [8, 9],   // Game 2
  [5, 12],  // Game 3
  [4, 13],  // Game 4
  [6, 11],  // Game 5
  [3, 14],  // Game 6
  [7, 10],  // Game 7
  [2, 15],  // Game 8
]

// Round 2 matchups (which Round 1 winners face each other)
// Index = Round 2 game number (0-3), Value = [R1 game A, R1 game B]
export const ROUND_2_MATCHUPS: [number, number][] = [
  [1, 2],  // Winner of 1v16 vs Winner of 8v9
  [3, 4],  // Winner of 5v12 vs Winner of 4v13
  [5, 6],  // Winner of 6v11 vs Winner of 3v14
  [7, 8],  // Winner of 7v10 vs Winner of 2v15
]

// Sweet 16 matchups (which Round 2 winners face each other)
export const SWEET_16_MATCHUPS: [number, number][] = [
  [1, 2],  // Top bracket
  [3, 4],  // Bottom bracket
]

// Elite 8 matchup (Sweet 16 winners)
export const ELITE_8_MATCHUP: [number, number] = [1, 2]

// Region positions for Final Four
export const FINAL_FOUR_MATCHUPS = {
  game1: ['top-left', 'bottom-left'],      // Semi 1
  game2: ['top-right', 'bottom-right'],    // Semi 2
}

export interface Team {
  id: number
  name: string
  seed: number
  region: string
  region_position: string
}

export interface GameSlot {
  year: number
  round: number
  region: string | null  // null for Final Four and Championship
  region_position: string | null
  bracket_position: number
  team1_id: number | null
  team2_id: number | null
  winner_id: number | null
  team1_score: number | null
  team2_score: number | null
  is_complete: boolean
}

/**
 * Generate all 63 game slots for a tournament
 * @param year - Tournament year
 * @param teams - Array of 64 teams with region_position set
 */
export async function generateAllGames(year: number, teams: Team[]): Promise<boolean> {
  // Group teams by region_position
  const teamsByPosition: Record<string, Team[]> = {
    'top-left': [],
    'top-right': [],
    'bottom-left': [],
    'bottom-right': [],
  }

  teams.forEach(team => {
    if (team.region_position && teamsByPosition[team.region_position]) {
      teamsByPosition[team.region_position].push(team)
    }
  })

  // Validate we have 16 teams per region
  for (const pos of Object.keys(teamsByPosition)) {
    if (teamsByPosition[pos].length !== 16) {
      console.error(`Region ${pos} has ${teamsByPosition[pos].length} teams, expected 16`)
      return false
    }
  }

  const games: GameSlot[] = []
  let globalPosition = 1

  // Generate games for each region (positions 1-60)
  const positions = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
  
  for (const position of positions) {
    const regionTeams = teamsByPosition[position]
    const region = regionTeams[0]?.region || ''

    // Round 1: 8 games (positions 1-8 within region)
    for (let i = 0; i < 8; i++) {
      const [seed1, seed2] = ROUND_1_MATCHUPS[i]
      const team1 = regionTeams.find(t => t.seed === seed1)
      const team2 = regionTeams.find(t => t.seed === seed2)

      games.push({
        year,
        round: 1,
        region,
        region_position: position,
        bracket_position: globalPosition++,
        team1_id: team1?.id || null,
        team2_id: team2?.id || null,
        winner_id: null,
        team1_score: null,
        team2_score: null,
        is_complete: false,
      })
    }

    // Round 2: 4 games (positions 9-12 within region)
    for (let i = 0; i < 4; i++) {
      games.push({
        year,
        round: 2,
        region,
        region_position: position,
        bracket_position: globalPosition++,
        team1_id: null,  // TBD - filled when R1 winners determined
        team2_id: null,
        winner_id: null,
        team1_score: null,
        team2_score: null,
        is_complete: false,
      })
    }

    // Sweet 16: 2 games (positions 13-14 within region)
    for (let i = 0; i < 2; i++) {
      games.push({
        year,
        round: 3,
        region,
        region_position: position,
        bracket_position: globalPosition++,
        team1_id: null,
        team2_id: null,
        winner_id: null,
        team1_score: null,
        team2_score: null,
        is_complete: false,
      })
    }

    // Elite 8: 1 game (position 15 within region)
    games.push({
      year,
      round: 4,
      region,
      region_position: position,
      bracket_position: globalPosition++,
      team1_id: null,
      team2_id: null,
      winner_id: null,
      team1_score: null,
      team2_score: null,
      is_complete: false,
    })
  }

  // Final Four: 2 games (positions 61-62)
  games.push({
    year,
    round: 5,
    region: null,
    region_position: null,
    bracket_position: 61,
    team1_id: null,  // Top-left region winner
    team2_id: null,  // Bottom-left region winner
    winner_id: null,
    team1_score: null,
    team2_score: null,
    is_complete: false,
  })

  games.push({
    year,
    round: 5,
    region: null,
    region_position: null,
    bracket_position: 62,
    team1_id: null,  // Top-right region winner
    team2_id: null,  // Bottom-right region winner
    winner_id: null,
    team1_score: null,
    team2_score: null,
    is_complete: false,
  })

  // Championship: 1 game (position 63)
  games.push({
    year,
    round: 6,
    region: null,
    region_position: null,
    bracket_position: 63,
    team1_id: null,  // Final Four game 1 winner
    team2_id: null,  // Final Four game 2 winner
    winner_id: null,
    team1_score: null,
    team2_score: null,
    is_complete: false,
  })

  // Delete existing games for this year
  await supabase.from('games').delete().eq('year', year)

  // Insert all games
  const { error } = await supabase.from('games').insert(games)
  
  if (error) {
    console.error('Error inserting games:', error)
    return false
  }

  return true
}

/**
 * Get the next game position that a winner advances to
 * @param currentPosition - Current game's bracket position
 * @param regionPosition - Region position (top-left, etc.)
 */
export function getNextGamePosition(currentPosition: number, regionPosition: string | null): { position: number, slot: 'team1' | 'team2' } | null {
  // Calculate position within region (1-15)
  const regionOffset = getRegionOffset(regionPosition)
  const posInRegion = currentPosition - regionOffset

  // Round 1 games (1-8) advance to Round 2 (9-12)
  if (posInRegion >= 1 && posInRegion <= 8) {
    const round2Game = Math.ceil(posInRegion / 2)  // 1,2→1, 3,4→2, 5,6→3, 7,8→4
    const slot = posInRegion % 2 === 1 ? 'team1' : 'team2'
    return { position: regionOffset + 8 + round2Game, slot }
  }

  // Round 2 games (9-12) advance to Sweet 16 (13-14)
  if (posInRegion >= 9 && posInRegion <= 12) {
    const sweet16Game = Math.ceil((posInRegion - 8) / 2)  // 9,10→1, 11,12→2
    const slot = (posInRegion - 8) % 2 === 1 ? 'team1' : 'team2'
    return { position: regionOffset + 12 + sweet16Game, slot }
  }

  // Sweet 16 games (13-14) advance to Elite 8 (15)
  if (posInRegion >= 13 && posInRegion <= 14) {
    const slot = posInRegion === 13 ? 'team1' : 'team2'
    return { position: regionOffset + 15, slot }
  }

  // Elite 8 (15) advances to Final Four
  if (posInRegion === 15) {
    if (regionPosition === 'top-left') return { position: 61, slot: 'team1' }
    if (regionPosition === 'bottom-left') return { position: 61, slot: 'team2' }
    if (regionPosition === 'top-right') return { position: 62, slot: 'team1' }
    if (regionPosition === 'bottom-right') return { position: 62, slot: 'team2' }
  }

  // Final Four games advance to Championship
  if (currentPosition === 61) return { position: 63, slot: 'team1' }
  if (currentPosition === 62) return { position: 63, slot: 'team2' }

  // Championship has no next game
  return null
}

/**
 * Get the starting bracket position offset for a region
 */
function getRegionOffset(regionPosition: string | null): number {
  switch (regionPosition) {
    case 'top-left': return 0
    case 'top-right': return 15
    case 'bottom-left': return 30
    case 'bottom-right': return 45
    default: return 0
  }
}

/**
 * Set a winner for a game and advance them to the next round
 */
export async function setGameWinner(
  gameId: number,
  winnerId: number,
  loserId: number,
  bracketPosition: number,
  regionPosition: string | null,
  year: number
): Promise<boolean> {
  // 1. Update the current game with winner and updated_at timestamp
  const { error: gameError } = await supabase
    .from('games')
    .update({ 
      winner_id: winnerId, 
      is_complete: true,
      updated_at: new Date().toISOString()
    })
    .eq('id', gameId)

  if (gameError) {
    console.error('Error updating game:', gameError)
    return false
  }

  // 2. Increment winner's wins (using tournament_teams table)
  const { data: winner } = await supabase
    .from('tournament_teams')
    .select('wins')
    .eq('id', winnerId)
    .single()

  await supabase
    .from('tournament_teams')
    .update({ wins: (winner?.wins || 0) + 1 })
    .eq('id', winnerId)

  // 3. Mark loser as eliminated (using tournament_teams table)
  await supabase
    .from('tournament_teams')
    .update({ is_eliminated: true })
    .eq('id', loserId)

  // 4. Advance winner to next game
  const nextGame = getNextGamePosition(bracketPosition, regionPosition)
  
  if (nextGame) {
    const updateField = nextGame.slot === 'team1' ? 'team1_id' : 'team2_id'
    await supabase
      .from('games')
      .update({ [updateField]: winnerId })
      .eq('year', year)
      .eq('bracket_position', nextGame.position)
  }

  return true
}

/**
 * Get round name
 */
export function getRoundName(round: number): string {
  switch (round) {
    case 1: return 'Round of 64'
    case 2: return 'Round of 32'
    case 3: return 'Sweet 16'
    case 4: return 'Elite 8'
    case 5: return 'Final Four'
    case 6: return 'Championship'
    default: return `Round ${round}`
  }
}

